import {useMemo} from 'react';
import {useAppSelector} from './redux';
import {selectTestsByName, selectSubscription} from '../slices/testSlice';
import {selectCurrentPatient} from '../slices/patientSlice';
import {generateDummyDataResponse} from '../utils/dummyData';

/**
 * Custom hook for dashboard components to get subscription-aware data
 * Returns real data if subscribed, dummy data if not subscribed
 * For Results pages, always use real data directly from Redux
 */
export const useDashboardData = () => {
    const currentPatient = useAppSelector(selectCurrentPatient);
    const subscription = useAppSelector(selectSubscription);

    // Get real data from Redux
    const realPatientData = useAppSelector((state) => selectTestsByName(state, currentPatient?.fullName || ''));

    // Generate dummy data when needed
    const dummyData = useMemo(() => {
        if (subscription) return null; // Don't generate if subscribed

        const currentPatientName =
            currentPatient?.firstName && currentPatient?.lastName
                ? {firstName: currentPatient.firstName, lastName: currentPatient.lastName}
                : {firstName: 'Sample', lastName: 'Patient'};

        return generateDummyDataResponse(currentPatientName);
    }, [subscription, currentPatient?.firstName, currentPatient?.lastName]);

    // Return appropriate data based on subscription status
    const dashboardData = useMemo(() => {
        if (subscription) {
            // User is subscribed - return real data
            return realPatientData;
        } else {
            // User is not subscribed - return dummy data
            return dummyData?.orders || [];
        }
    }, [subscription, realPatientData, dummyData]);

    return {
        data: dashboardData,
        isSubscribed: subscription,
    };
};

export default useDashboardData;
